import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { HistoryTiming } from "@faire/web--source/common/performance/HistoryTiming";
import { useRenderTracing } from "@faire/web--source/common/tracing/useRenderTracing";
import { Button } from "@faire/web--source/slate/Button";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackProductInfoClickOnImageInMediaGridClick } from "@faire/web-api--source/events/productInfo/click/clickOnImageInMediaGrid";
import { observer } from "mobx-react";
import React, { Suspense, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { ImageGridView, ShowAllContainer } from "@faire/retailer/views/product/__internal__/styled";
import { trackPreloadedImageGrid } from "@faire/retailer/views/product/preload/trackPreloadedImageGrid";
import { buildProductImageGridSrcSet, IMAGE_GRID_MAX_IMAGES_VISIBLE } from "@faire/retailer/views/product/ProductImages/utils";
import { LazyVideoPlayer } from "@faire/retailer/views/video/components/VideoPlayer";
var ShowAll = function(param) {
    var productMedia = param.productMedia, onClick = param.onClick;
    if (productMedia.length <= IMAGE_GRID_MAX_IMAGES_VISIBLE) {
        return null;
    }
    return /*#__PURE__*/ _jsx(ShowAllContainer, {
        "data-test-id": "show-all-media-button",
        children: /*#__PURE__*/ _jsx(Button, {
            variant: "secondary",
            onClick: onClick,
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "eLmElb",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Show All "
                    },
                    {
                        type: 1,
                        value: "imageTotalNumber"
                    },
                    {
                        type: 0,
                        value: " Photos"
                    }
                ],
                values: {
                    imageTotalNumber: productMedia.length
                }
            })
        })
    });
};
var VideoMedia = function(param) {
    var video = param.video, index = param.index, showVerticalImagery = param.showVerticalImagery, onLoadedMetadata = param.onLoadedMetadata;
    return /*#__PURE__*/ _jsx(MediaContainer, {
        $index: index,
        $ratio: showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: null,
            children: /*#__PURE__*/ _jsx(LazyVideoPlayer, {
                video: video,
                videoOptions: {
                    autoplay: true,
                    muted: true,
                    loop: true
                },
                onLoadedMetaData: onLoadedMetadata
            })
        })
    }, video.url);
};
var ImageMedia = function(param) {
    var image = param.image, index = param.index, alt = param.alt, showVerticalImagery = param.showVerticalImagery, onClick = param.onClick, onLoad = param.onLoad;
    var isTabletAndAbove = useViewport().isTabletAndAbove;
    var _buildProductImageGridSrcSet = buildProductImageGridSrcSet(image, showVerticalImagery, isTabletAndAbove), src = _buildProductImageGridSrcSet.src, srcSet = _buildProductImageGridSrcSet.srcSet;
    return /*#__PURE__*/ _jsx(MediaContainer, {
        $index: index,
        $ratio: showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1,
        children: /*#__PURE__*/ _jsx(StyledImage, {
            onClick: function() {
                return onClick(index);
            },
            $hideBorder: true,
            $ratio: showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1,
            $width: "100%",
            children: /*#__PURE__*/ _jsx("img", {
                srcSet: srcSet,
                src: src,
                alt: alt,
                onLoad: onLoad
            })
        })
    }, src);
};
var ImageGridFn = function(param) {
    var setIndex = param.setIndex, openImageGallery = param.openImageGallery, productMedia = param.productMedia, showVerticalImagery = param.showVerticalImagery, showroomData = param.showroomData, alt = param.alt;
    var showAllClick = function() {
        setIndex(0);
        openImageGallery();
    };
    var handleClick = function(index) {
        trackProductInfoClickOnImageInMediaGridClick(index.toString(), productMedia.length.toString());
        setIndex(index);
        openImageGallery();
    };
    var hasMedia = productMedia.length > 0;
    var imageGridRef = useRef(null);
    var mediaToShow = productMedia.slice(0, IMAGE_GRID_MAX_IMAGES_VISIBLE);
    var _useState = _sliced_to_array(useState(0), 2), loadedCount = _useState[0], setLoadedCount = _useState[1];
    var endTrace = useRenderTracing("ImageGrid");
    useEffect(function() {
        if (loadedCount >= mediaToShow.length) {
            endTrace();
        }
    }, [
        loadedCount,
        mediaToShow.length,
        endTrace
    ]);
    useEffect(function() {
        if (hasMedia && imageGridRef.current && !HistoryTiming.get().isInitialLoad) {
            trackPreloadedImageGrid(imageGridRef.current, (showroomData === null || showroomData === void 0 ? void 0 : showroomData.showVerticalImagery) || false);
        }
    }, [
        hasMedia,
        showroomData === null || showroomData === void 0 ? void 0 : showroomData.showVerticalImagery
    ]);
    if (!hasMedia) {
        return null;
    }
    var onLoad = function() {
        setLoadedCount(function(prevCount) {
            return prevCount + 1;
        });
    };
    return /*#__PURE__*/ _jsxs(ImageGridView, {
        ref: imageGridRef,
        $mediaTotal: mediaToShow.length,
        "data-test-id": "image-section",
        children: [
            mediaToShow.map(function(media, index) {
                if (media.isVideo) {
                    return /*#__PURE__*/ _jsx(React.Fragment, {
                        children: /*#__PURE__*/ _jsx(VideoMedia, {
                            video: media.video,
                            index: media.index,
                            showVerticalImagery: showVerticalImagery,
                            onLoadedMetadata: onLoad
                        })
                    }, index);
                } else {
                    return /*#__PURE__*/ _jsx(React.Fragment, {
                        children: /*#__PURE__*/ _jsx(ImageMedia, {
                            image: media.image,
                            index: media.index,
                            onClick: handleClick,
                            showVerticalImagery: showVerticalImagery,
                            alt: alt,
                            onLoad: onLoad
                        })
                    }, index);
                }
            }),
            /*#__PURE__*/ _jsx(ShowAll, {
                productMedia: productMedia,
                onClick: showAllClick
            })
        ]
    });
};
export var ImageGrid = observer(ImageGridFn);
var MediaContainer = styled.div.withConfig({
    componentId: "sc-f6e0a1c3-0"
})([
    "grid-area:",
    ";overflow:hidden;",
    "{aspect-ratio:",
    ";}filter:brightness(98%);background-color:",
    ";",
    "{display:inline-block;width:inherit;height:100vw;}"
], function(props) {
    return css([
        "media-",
        ""
    ], props.$index);
}, responsiveMediaQueries.tabletAndAbove, function(props) {
    return props.$ratio;
}, Core.surface.inverse, responsiveMediaQueries.mobile);
var StyledImage = styled(AspectRatioPicture).withConfig({
    componentId: "sc-f6e0a1c3-1"
})([
    "cursor:zoom-in;transition:transform 0.5s;height:100%;",
    "{&:hover{transform:scale(1.05);}}"
], responsiveMediaQueries.tabletAndAbove);
